import type { CampaignCardData } from "@cafe/shared";
import { formatInt, formatMXN } from "@cafe/shared";
import { useQuery } from "@tanstack/react-query";
import { CampaignCard } from "../components/tools/CampaignCard";
import { Badge, Card, Spinner } from "../components/ui/primitives";
import { api, unwrap } from "../lib/api";

export function CampaignsPage() {
  const local = useQuery({ queryKey: ["campaigns"], queryFn: async () => unwrap(await api.campaigns.$get()) });
  const history = useQuery({ queryKey: ["campaigns", "history"], queryFn: async () => unwrap(await api.campaigns.history.$get({ query: {} })) });

  return (
    <div className="h-full overflow-y-auto px-3 py-4 sm:px-6">
      <div className="mx-auto max-w-4xl space-y-6">
        <section>
          <h2 className="mb-2 text-lg font-semibold">Creadas con el copiloto</h2>
          {local.isLoading && <Spinner />}
          {local.data?.campaigns.length === 0 && (
            <p className="text-sm text-roast/70">Aún no hay campañas. Pídele una al copiloto, por ejemplo: “arma una campaña para recuperar clientes en riesgo”.</p>
          )}
          <div className="space-y-3">
            {local.data?.campaigns.map((c) => (
              <CampaignCard key={c.local_id} initial={c as CampaignCardData} />
            ))}
          </div>
        </section>

        <section>
          <h2 className="mb-2 text-lg font-semibold">Historial y resultados (Luyoa)</h2>
          {history.isLoading && <Spinner />}
          {history.error && <p className="text-sm text-red-700">{(history.error as Error).message}</p>}
          <div className="grid gap-3 sm:grid-cols-2">
            {history.data?.campaigns
              .filter((c) => !c.simulated)
              .map((c) => (
                <Card key={c.id} className="p-3 text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{c.name}</p>
                      <p className="text-xs text-roast/80">
                        {c.channel} · {c.valid}
                      </p>
                    </div>
                    <Badge tone={c.results?.lift_pp != null && c.results.lift_pp > 0 ? "good" : "neutral"}>
                      {c.results?.lift_pp != null ? `Lift ${c.results.lift_pp > 0 ? "+" : ""}${c.results.lift_pp} pp` : "Sin grupo control"}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-roast/80">{c.audience}</p>
                  {c.warnings.map((w) => (
                    <p key={w} className="mt-1 text-[11px] text-amber-800">⚠ {w}</p>
                  ))}
                  {c.results && (
                    <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                      <Stat label="Enviados" value={formatInt(c.results.sent ?? 0)} />
                      <Stat label="Canjes" value={`${formatInt(c.results.redeemed ?? 0)} (${c.results.redemption_rate_pct ?? 0}%)`} />
                      <Stat label="Ingreso neto atrib." value={formatMXN(c.results.attributed_revenue_net ?? 0)} />
                      <Stat label="Costo de oferta" value={formatMXN(c.results.offer_cost ?? 0)} />
                      <Stat label="Utilidad atrib." value={formatMXN(c.results.attributed_gross_profit ?? 0)} />
                      <Stat
                        label="Visita trat./ctrl."
                        value={`${((c.results.treatment_visit_rate ?? 0) * 100).toFixed(1)}% / ${c.results.control_visit_rate != null ? `${(c.results.control_visit_rate * 100).toFixed(1)}%` : "—"}`}
                      />
                    </dl>
                  )}
                </Card>
              ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-crema px-2 py-1.5">
      <dt className="text-[10px] text-roast/70">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
    </div>
  );
}
